import { inject, Injectable } from '@angular/core';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import {
  distinctUntilChanged,
  EMPTY,
  filter,
  map,
  merge,
  Observable,
  of,
  scan,
  switchMap,
} from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import {
  findWorkflowGetSourceNode,
  getActiveBinding,
  GlobalQueryState,
} from '@blaze-shared/elements-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import {
  createElementSchemaId,
  DatasetColumn,
  type ElementSchema,
} from '@blaze-shared/schema-domain';
import { isDeepEqual } from '@blaze-shared/shared-util';

import { hasDataProps } from '../../../element-model/element-props-guards';
import { DataProviderQuery, InputDataPackage } from '../../io-data.models';

import { DataSourceProvider } from './data-source.data-provider';

@Injectable({ providedIn: 'root' })
export class DataFilterDataProvider implements DataSourceProvider {
  private readonly schemaService = inject(SchemaService);
  private readonly store = inject(Store);
  readonly sourceType = 'data-filter' as const;

  getDataStream({ element }: DataProviderQuery): Observable<InputDataPackage> {
    const binding = hasDataProps(element) ? getActiveBinding(element.props.data) : undefined;
    if (!element || !binding?.dataSource?.id) {
      return EMPTY;
    }

    return this.store.select(runtimeSelectors.selectWorkflowById(binding.dataSource.id)).pipe(
      filter((wf) => !wf?.isDraft),
      distinctUntilChanged(isDeepEqual),
      concatLatestFrom(() => this.store.select(runtimeSelectors.selectConnections)),
      map(([workflow, connections]) => {
        const node = findWorkflowGetSourceNode(workflow);
        if (!workflow || !node) {
          throw new Error(`Get node not found in ${workflow?.id}`);
        }

        const inputSchemasMap = Object.entries(workflow.inputs).map(([name, connectionId]) => {
          const elementInputId = connections.find((c) => c.id === connectionId)?.source.id;
          if (!elementInputId) {
            throw new Error(`Workflow ${workflow.id} input connection missing`);
          }
          const schemaId = createElementSchemaId(elementInputId, 'change');
          return { name, schemaId };
        });

        return { node, workflowId: workflow.id, inputSchemasMap };
      }),
      switchMap(({ node, workflowId, inputSchemasMap }) => {
        const inputSchemas$ = inputSchemasMap.map(({ name, schemaId }) =>
          this.store.select(runtimeSelectors.selectSchemaById(schemaId)).pipe(
            distinctUntilChanged(isDeepEqual),
            map((schema) => ({ name, schema })),
          ),
        );

        let input$ = of({ columns: {} as Record<string, DatasetColumn> });
        if (inputSchemas$.length) {
          input$ = merge(...inputSchemas$).pipe(
            scan(
              ({ columns }, schemaResult) => {
                if (schemaResult.schema?.columns.length) {
                  const columnToAdd = {
                    ...schemaResult.schema.columns[0],
                    name: schemaResult.name,
                  };
                  columns[columnToAdd.name] = columnToAdd;
                }
                return {
                  columns,
                  ready: Object.keys(columns).length === inputSchemas$.length,
                };
              },
              {
                columns: {} as Record<string, DatasetColumn>,
                ready: false,
              },
            ),
            filter((columnsResult) => columnsResult.ready),
          );
        }

        return input$.pipe(
          map(({ columns }) => {
            if (!node.datasetId) {
              throw new Error(`Get node is missing datasetId field, in: ${workflowId}`);
            }
            const data: GlobalQueryState = {
              elementId: element.id,
              query: node.query,
              datasetId: node.datasetId,
              workflowId,
            };
            const datasetSchemaName = node.datasetId
              ? this.schemaService.getSchema(node.datasetId)?.name
              : undefined;
            const schema: ElementSchema = {
              id: createElementSchemaId(element.id, 'change'),
              name: `DataFilter_${datasetSchemaName ?? node.datasetId}`,
              elementId: element.id,
              columns: Object.values(columns),
              eventType: 'change',
              datasetId: node.datasetId,
              workflowId,
            };

            return <InputDataPackage>{
              sourceType: 'data-filter',
              data,
              schema,
            };
          }),
          distinctUntilChanged(isDeepEqual),
        );
      }),
    );
  }
}
